import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '../lib/api.js'
import { isDrayNowHost } from '../lib/config/multitenancy.js'
import { KindeTokenAdapter } from '../features/auth/lib/KindeTokenAdapter.js'
import { ROLES, resolveEffectiveRole } from '../lib/config/roles.js'

/**
 * Reads the stored Kinde token. Returns null when absent or unparseable —
 * callers treat that as "no token available".
 */
const readKindeToken = () => {
  try {
    const raw = localStorage.getItem('kindeToken')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * The `rol` claim from the stored Kinde token, or null. `extractTokenData`
 * returns null for a token with no valid non-excluded role.
 */
const readTokenRole = (token = readKindeToken()) =>
  token ? (KindeTokenAdapter.extractTokenData(token)?.rol ?? null) : null

/**
 * Whether the stored Kinde token grants `permission`. Reads the token claim,
 * not `user.permissions`: svc-marketplace authorizes from the claim, and a
 * Kinde grant never updates the DB copy that `user.permissions` mirrors.
 *
 * @param {string} permission - Kinde permission key, e.g. 'canManageTranslations'
 * @returns {boolean}
 */
export const hasTokenPermission = (permission) => {
  const token = readKindeToken()
  if (!token) return false
  const { mkt } = KindeTokenAdapter.extractPermissions(token)
  return Array.isArray(mkt) && mkt.includes(permission)
}

/**
 * Auth Store - Manages user authentication state
 *
 * The isAuthenticated DECISION is made in one place only — AuthGate, which
 * syncs the Kinde SDK's settled answer here before any route renders. All
 * guards and pages read this store; none may re-ask the SDK.
 * fetchUser() fetches the user record from the API.
 */
export const useAuthStore = create(
  persist(
    (set) => ({
      // State
      user: null,
      isAuthenticated: false,
      // True once this SPA lifetime has seen an authenticated session. Lets
      // ProtectedLayout tell "arrived logged-out at a protected URL" (stash
      // the destination for after login) apart from "auth flipped off
      // mid-session" (logout/teardown — do NOT stash the page they were on).
      // Never persisted; a full page load resets it.
      hasAuthenticatedThisSession: false,

      // Actions
      setUser: (user) =>
        set((state) => ({
          user,
          isAuthenticated: !!user,
          hasAuthenticatedThisSession:
            state.hasAuthenticatedThisSession || !!user,
        })),
      setAuthenticated: (value) =>
        set((state) => ({
          isAuthenticated: value,
          hasAuthenticatedThisSession:
            state.hasAuthenticatedThisSession || value,
        })),
      clearUser: () => set({ user: null, isAuthenticated: false }),

      /**
       * Derives the marketplace redirect category from the stored Kinde token.
       */
      getRedirectCategory: () => {
        try {
          const token = readKindeToken()
          if (!token) return 'drafts'
          const permissions = KindeTokenAdapter.extractPermissions(token)
          const mkt = permissions.mkt || []
          if (!mkt.includes('canViewOpenMarketplace')) return 'tendered'
          if (isDrayNowHost()) return 'open'
          // Runs before the DB user is fetched (login, /callback), so the token
          // is the only role source available here.
          const role = resolveEffectiveRole({ tokenRole: readTokenRole(token) })
          if (role === ROLES.CARRIER) return 'pending_carrier'
          return 'drafts'
        } catch {
          return 'drafts'
        }
      },

      /**
       * Fetches user data from the API by UID, maps to user shape, and sets state.
       * @param {string} uid - The user's unique identifier
       * @returns {Promise<Object>} The user data
       */
      fetchUser: async (uid) => {
        try {
          const data = await api.getUserById(uid)
          const user = {
            uid: data.uid,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone,
            companyUid: data.companyUid,
            company: data.company,
            tenant: data.tenant,
            // Effective role. Resolved here — the only live write path into
            // `user` — so every role check downstream agrees without each one
            // having to know that two role sources exist.
            role: resolveEffectiveRole({
              tokenRole: readTokenRole(),
              accountRole: data.role,
            }),
            // Raw DB values, so the drift stays observable (posthogIdentify
            // reports accountRole alongside role).
            accountRole: data.role,
            roles: data.roles,
            permissions: data.permissions,
            properties: data.properties,
          }
          set({ user, isAuthenticated: true })
          return user
        } catch (error) {
          console.error('Zustand authStore: Failed to fetch user', error)
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      // Do not persist isAuthenticated — it must be re-derived by AuthGate
      // on every page load so there is no stale "logged in" state after a
      // logout or token expiry.
      partialize: (state) => ({ user: state.user }),
      // Re-resolve the effective role on rehydrate. A warm session renders
      // straight from persistence before bootstrap's fetchUser lands
      // (ProtectedLayout only holds the route while `user` is absent, and
      // role-derived init-once effects never re-run), so the persisted role
      // would otherwise be one resolved against an older token.
      //
      // `accountRole ?? role` covers users persisted before accountRole
      // existed, whose stored `role` IS the raw DB value.
      merge: (persisted, current) => {
        const merged = { ...current, ...persisted }
        if (!merged.user) return merged
        return {
          ...merged,
          user: {
            ...merged.user,
            role: resolveEffectiveRole({
              tokenRole: readTokenRole(),
              accountRole: merged.user.accountRole ?? merged.user.role,
            }),
          },
        }
      },
    },
  ),
)
